import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/** All supported themes.
 *  - 'light'      : warm parchment (default)
 *  - 'graphite'   : warm charcoal dark (default dark, replaces old 'dark')
 *  - 'midnight'   : deep purple-black (original dark mode)
 *  - 'nord'       : cool blue-grey
 *  - 'solarized'  : warm amber-teal dark
 *  - 'system'     : follow OS preference → resolves to light or graphite
 */
export const VALID_THEMES = ['light', 'graphite', 'midnight', 'nord', 'solarized', 'system'];

/** Apply a theme value to the <html> element. */
export function applyThemeToDOM(mode) {
  const root = document.documentElement;
  const dark = mode !== 'light';
  root.classList.toggle('dark', dark);
  if (!dark) {
    root.removeAttribute('data-theme');
  } else {
    // 'graphite' is the default .dark — no data-theme needed
    root.setAttribute('data-theme', mode === 'graphite' ? '' : mode);
  }
}

/** The Loom theme manager. Persists to localStorage AND to the user's server
 *  profile so the choice survives cross-device logins.
 *
 *  Backward compat: stored 'dark' maps to 'graphite'.
 */
export function useTheme() {
  const [themeMode, _setTheme] = useState(() => {
    const stored = localStorage.getItem('pw-theme');
    // Migrate 'system' / 'dark' / missing → 'graphite' (dark amber, the app default).
    // Users who explicitly picked another named theme keep their choice.
    if (!stored || stored === 'system' || stored === 'dark') {
      localStorage.setItem('pw-theme', 'graphite');
      return 'graphite';
    }
    return stored;
  });
  const suppressSave = useRef(false);

  // Apply the active theme to the DOM + persist to localStorage
  useEffect(() => {
    const apply = () => {
      const resolved = themeMode === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'graphite' : 'light')
        : themeMode;
      applyThemeToDOM(resolved);
    };
    apply();
    localStorage.setItem('pw-theme', themeMode);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => themeMode === 'system' && apply();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [themeMode]);

  // Save to server whenever themeMode changes — but skip if it was a server-initiated sync
  useEffect(() => {
    if (suppressSave.current) {
      suppressSave.current = false;
      return;
    }
    axios
      .put(`${API}/profile/me`, { theme_mode: themeMode }, { withCredentials: true })
      .catch(() => { /* best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMode]);

  // Listen for the `pw:theme-synced` event dispatched by auth_context after login
  useEffect(() => {
    const handler = (e) => {
      const raw = e.detail;
      // migrate old 'dark' value from server
      const mode = raw === 'dark' ? 'graphite' : raw;
      if (mode && VALID_THEMES.includes(mode) && mode !== themeMode) {
        suppressSave.current = true;
        _setTheme(mode);
      }
    };
    window.addEventListener('pw:theme-synced', handler);
    return () => window.removeEventListener('pw:theme-synced', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setThemeMode = (mode) => {
    suppressSave.current = false;
    _setTheme(mode);
  };

  return { themeMode, setThemeMode };
}
